import React from 'react';
import PromptCard from '../PromptCard';

interface CapstoneGuideProps {
  onPromptClick: (prompt: string) => void;
}

// Plain-language examples — no technical terms. Students say what they want to
// build; Capstone works out the language/approach (and asks if it's unsure).
const CapstoneGuide: React.FC<CapstoneGuideProps> = ({ onPromptClick }) => {
  const gamePrompts = [
    'A snake game I can play with the arrow keys',
    'Two-player Pong on one keyboard',
    'A flappy-bird style game where I tap to fly',
    'A brick-breaker game with a paddle and a ball',
    'A simple 3D game where I roll a ball around and collect coins',
  ];

  const appPrompts = [
    'A to-do list app that remembers my tasks',
    'A quiz game about world capitals that keeps score',
    'A tip calculator that splits the bill between friends',
    'A unit converter for length, weight, and temperature',
    'A password strength checker that gives tips',
  ];

  const dataPrompts = [
    'Turn a list of my monthly expenses into a chart',
    'Show the most common words in a paragraph I paste in',
    'A program that sorts my grocery list alphabetically',
    'A simple budget summary from my income and spending',
  ];

  return (
    <div className="documentation-content">
      <h1>Capstone - Build Real Projects</h1>
      <p className="intro-text">
        Describe what you want to build in your own words — no coding terms needed.
        Capstone plans it, writes the code, runs it, and (for games) lets you play it
        right in the browser. If it's unsure what you mean, it'll ask first. Tap any idea to start!
      </p>

      <div className="section">
        <h2>🎮 Games (play in the browser)</h2>
        <p className="section-desc">2D and simple 3D — single-player or two players on one keyboard</p>
        {gamePrompts.map((prompt, index) => (
          <PromptCard key={index} prompt={prompt} onClick={onPromptClick} />
        ))}
      </div>

      <div className="section">
        <h2>🧩 Apps &amp; tools</h2>
        <p className="section-desc">Small, useful programs you can run and share</p>
        {appPrompts.map((prompt, index) => (
          <PromptCard key={index} prompt={prompt} onClick={onPromptClick} />
        ))}
      </div>

      <div className="section">
        <h2>📊 Data &amp; charts</h2>
        <p className="section-desc">Turn everyday data into results and visuals</p>
        {dataPrompts.map((prompt, index) => (
          <PromptCard key={index} prompt={prompt} onClick={onPromptClick} />
        ))}
      </div>

      <div className="section">
        <h2>Tips</h2>
        <p className="section-desc">
          Just say what you want — Capstone picks the language and tools for you. Games run in your
          browser (one player or two on the same keyboard). Everything else runs and shows its output
          as it builds. The first build can take a couple of minutes while your environment starts up.
        </p>
      </div>
    </div>
  );
};

export default CapstoneGuide;
